import { useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import "./OrganizationDocuments.css";
import {
  Checkbox,
  Input,
  NoticeBox,
  FileText,
  Camera,
  ImagePlus,
  FileText as ExcelFile,
} from "../../../ui";
import DocumentCard from "../../components/DocumentCard/DocumentCard";
import DeleteSelectionToolbar from "../../components/DeleteSelectionToolbar/DeleteSelectionToolbar";
import useSelectionDelete from "../../hooks/useSelectionDelete";
import {
  formatDate,
  daysUntil,
  getLudFileName,
  openLudFile,
} from "../../utils/ludDocuments.helpers";
import {
  deleteOrganizationDocumentAPI,
  permanentDeleteOrganizationDocumentAPI,
  updateOrganizationDocumentAPI,
} from "../../../../utils/api/ludDocuments";
import { DocumentHistoryModal } from "../../../modals/DocumentHistory";
import { DeleteModal } from "../../../modals/Delete";
import PermanentDeleteModal from "../../components/PermanentDeleteModal/PermanentDeleteModal";

// Activate with Enter/Space as well as click (for non-button selectable rows).
const onActivateKey = (handler) => (event) => {
  if (event.target !== event.currentTarget) return;
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    handler();
  }
};

export default function OrganizationDocuments({
  restaurant,
  lang,
  updateRestaurant,
  token,
  refreshRestaurant,
  uploadTarget,
  onUploadTargetChange,
  canPermanentDelete = false,
  canManage,
  onError,
}) {
  const ar = lang === "ar";
  const [historyTarget, setHistoryTarget] = useState(null);
  const normalDelete = useSelectionDelete({
    deleteOne: (key) => deleteOrganizationDocumentAPI(token, restaurant.id, key),
    refresh: refreshRestaurant,
    lang,
  });
  const { can: canAccess } = useAccess();
  const permanentDelete = useSelectionDelete({
    // handler-level authorization, against this restaurant's public id
    deleteOne: (key) =>
      canAccess("ludDocument.permanentDelete", { restaurantId: restaurant.id })
        ? permanentDeleteOrganizationDocumentAPI(token, restaurant.id, key)
        : Promise.reject(new Error("Not allowed")),
    refresh: refreshRestaurant,
    lang,
  });

  // The two selection modes are mutually exclusive.
  const startNormalDelete = () => {
    permanentDelete.cancel();
    normalDelete.start();
  };
  const startPermanentDelete = () => {
    normalDelete.cancel();
    permanentDelete.start();
  };
  const activeSelection = permanentDelete.active ? permanentDelete : normalDelete;

  const openFile = (file, reference) =>
    openLudFile(file, {
      token,
      storeId: restaurant.id,
      kind: "organization",
      reference,
      onError,
    });

  const updateExpiry = async (key, value) => {
    updateRestaurant((current) => ({
      ...current,
      orgDocs: current.orgDocs.map((doc) =>
        doc.key === key ? { ...doc, expiryDate: value } : doc,
      ),
    }));
    try {
      await updateOrganizationDocumentAPI(token, restaurant.id, key, {
        expiryDate: value,
      });
    } catch (error) {
      onError?.(
        error,
        ar ? "لم يتم حفظ تاريخ الانتهاء" : "The expiry date was not saved",
      );
    }
    // Re-sync either way (also reverts the optimistic value after a failure).
    await refreshRestaurant().catch((error) => onError?.(error));
  };

  const hasCurrentDocuments =
    [
      ...restaurant.orgDocs.filter((doc) => doc.currentFile),
      restaurant.productPhotosFile ? { key: "productPhotos" } : null,
      restaurant.logoFile ? { key: "logo" } : null,
      restaurant.restaurantMenuFile ? { key: "restaurantMenu" } : null,
      restaurant.deliveryAppsMenuFile ? { key: "deliveryAppsMenu" } : null,
      restaurant.foodCostSheetFile ? { key: "foodCostSheet" } : null,
    ].filter(Boolean).length > 0;

  const hasPermanentDocuments =
    hasCurrentDocuments ||
    restaurant.orgDocs.some((doc) => doc.history?.length) ||
    Boolean(restaurant.productPhotosHistory?.length) ||
    Boolean(restaurant.logoHistory?.length) ||
    Boolean(restaurant.restaurantMenuHistory?.length) ||
    Boolean(restaurant.deliveryAppsMenuHistory?.length) ||
    Boolean(restaurant.foodCostSheetHistory?.length);

  // Checkbox shown for current files in normal mode, and for current files or
  // history in permanent mode (same rules as before).
  const selectionCheckbox = (key, hasFile, hasHistory, className = "lud-document-delete-checkbox") =>
    (normalDelete.active && hasFile) ||
    (permanentDelete.active && (hasFile || hasHistory)) ? (
      <div className={className} onClick={(event) => event.stopPropagation()}>
        <Checkbox
          checked={activeSelection.isSelected(key)}
          onChange={() => activeSelection.toggle(key)}
        />
      </div>
    ) : null;

  const historyLink = (title, history, reference) =>
    history?.length ? (
      <button
        type="button"
        className="lud-document-history-link"
        onClick={(event) => {
          event.stopPropagation();
          setHistoryTarget({ title, history, kind: "organization", reference });
        }}
      >
        {ar
          ? `يوجد ${history.length} نسخة سابقة ملغاة — عرض السجل`
          : `${history.length} previous superseded versions — View history`}
      </button>
    ) : (
      ""
    );

  const fileLink = (file, reference) =>
    file ? (
      <button
        type="button"
        className="lud-org-file-link"
        onClick={(event) => {
          event.stopPropagation();
          openFile(file, reference);
        }}
      >
        {getLudFileName({
          file,
          kind: "organization",
          reference,
          originalName: file.originalName,
        })}
      </button>
    ) : null;

  return (
    <>
      {canManage &&
        (hasCurrentDocuments ||
          (canPermanentDelete && hasPermanentDocuments)) && (
          <div className="lud-delete-actions-row">
            {hasCurrentDocuments ? (
              <DeleteSelectionToolbar
                selection={normalDelete}
                lang={lang}
                onStart={startNormalDelete}
              />
            ) : (
              <span />
            )}

            {canPermanentDelete && hasPermanentDocuments ? (
              <DeleteSelectionToolbar
                selection={permanentDelete}
                lang={lang}
                permanent
                className="lud-delete-toolbar lud-delete-toolbar--permanent"
                onStart={startPermanentDelete}
              />
            ) : null}
          </div>
        )}

      <div className="lud-doc-grid">
        {restaurant.orgDocs.map((doc) => {
          const missing = !doc.addedDate;
          const expiryDays = daysUntil(doc.expiryDate);
          let status = ar ? "سارٍ" : "Valid";
          let statusVariant = "success";

          if (missing) {
            status = ar ? "غير مرفوع" : "Not uploaded";
            statusVariant = "danger";
          } else if (doc.key === "cr" && expiryDays !== null) {
            if (expiryDays < 0) {
              status = ar ? "منتهي" : "Expired";
              statusVariant = "danger";
            } else if (expiryDays <= 30) {
              status = ar
                ? `ينتهي خلال ${expiryDays} يوم`
                : `Expires in ${expiryDays} days`;
              statusVariant = "warning";
            } else {
              status = ar
                ? `ساري حتى ${formatDate(doc.expiryDate, lang)}`
                : `Valid until ${formatDate(doc.expiryDate, lang)}`;
            }
          }

          return (
            <DocumentCard
              key={doc.key}
              onClick={() => onUploadTargetChange?.({ documentKey: doc.key })}
              selected={uploadTarget?.documentKey === doc.key}
              title={ar ? doc.nameAr : doc.nameEn}
              subtitle={
                missing
                  ? ar
                    ? "لم يُرفع بعد"
                    : "Not uploaded yet"
                  : `${ar ? "أُضيف بتاريخ" : "Added on"} ${formatDate(doc.addedDate, lang)}`
              }
              badge={
                doc.essential
                  ? ar
                    ? "أساسي"
                    : "Essential"
                  : ar
                    ? "ثانوي"
                    : "Secondary"
              }
              badgeVariant={doc.essential ? "danger" : "primary"}
              meta={missing ? "—" : ar ? "مستند PDF" : "PDF document"}
              status={status}
              statusVariant={statusVariant}
              icon={<FileText size={21} />}
              historyText={historyLink(
                ar ? doc.nameAr : doc.nameEn,
                doc.history,
                doc.key,
              )}
            >
              {selectionCheckbox(doc.key, doc.currentFile, doc.history?.length)}
              {fileLink(doc.currentFile, doc.key)}
              {doc.key === "cr" ? (
                <div
                  className="lud-org-expiry"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Input
                    type="date"
                    label={ar ? "تاريخ الانتهاء" : "Expiry date"}
                    value={doc.expiryDate || ""}
                    onChange={(event) =>
                      updateExpiry(doc.key, event.target.value)
                    }
                  />
                </div>
              ) : null}
            </DocumentCard>
          );
        })}

        <DocumentCard
          selected={uploadTarget?.documentKey === "productPhotos"}
          onClick={() =>
            onUploadTargetChange?.({ documentKey: "productPhotos" })
          }
          title={ar ? "صور المنتجات" : "Product photos"}
          subtitle={
            restaurant.productPhotosAdded
              ? `${ar ? "أُضيفت بتاريخ" : "Added on"} ${formatDate(
                  restaurant.productPhotosAdded,
                  lang,
                )}`
              : ar
                ? "لم تُرفع بعد"
                : "Not uploaded yet"
          }
          badge={
            restaurant.productPhotosAdded
              ? ar
                ? "أساسي"
                : "Essential"
              : ar
                ? "ثانوي"
                : "Secondary"
          }
          badgeVariant={restaurant.productPhotosAdded ? "danger" : "primary"}
          meta={ar ? "مجموعة صور" : "Image collection"}
          status={
            restaurant.productPhotosAdded
              ? ar
                ? "مكتملة"
                : "Complete"
              : ar
                ? "غير مكتملة"
                : "Incomplete"
          }
          statusVariant={restaurant.productPhotosAdded ? "success" : "danger"}
          icon={<Camera size={21} />}
          historyText={historyLink(
            ar ? "صور المنتجات" : "Product photos",
            restaurant.productPhotosHistory,
            "productPhotos",
          )}
        >
          {selectionCheckbox(
            "productPhotos",
            restaurant.productPhotosFile,
            restaurant.productPhotosHistory?.length,
          )}
          {fileLink(restaurant.productPhotosFile, "productPhotos")}
        </DocumentCard>

        <DocumentCard
          selected={uploadTarget?.documentKey === "logo"}
          onClick={() => onUploadTargetChange?.({ documentKey: "logo" })}
          title={ar ? "شعار المطعم" : "Restaurant logo"}
          subtitle={`${ar ? "أُضيف بتاريخ" : "Added on"} ${formatDate(
            restaurant.logoAdded,
            lang,
          )}`}
          badge={ar ? "أساسي" : "Essential"}
          badgeVariant="danger"
          meta={ar ? "ملف صورة" : "Image file"}
          status={
            restaurant.logoFile
              ? ar
                ? "موجود"
                : "Available"
              : ar
                ? "غير مرفوع"
                : "Not uploaded"
          }
          statusVariant={restaurant.logoFile ? "success" : "danger"}
          icon={<ImagePlus size={21} />}
          historyText={historyLink(
            ar ? "شعار المطعم" : "Restaurant logo",
            restaurant.logoHistory,
            "logo",
          )}
        >
          {selectionCheckbox(
            "logo",
            restaurant.logoFile,
            restaurant.logoHistory?.length,
          )}
          {fileLink(restaurant.logoFile, "logo")}
        </DocumentCard>

        <DocumentCard
          selected={[
            "restaurantMenu",
            "deliveryAppsMenu",
            "foodCostSheet",
          ].includes(uploadTarget?.documentKey)}
          title={ar ? "معلومات المنيو" : "Menu information"}
          subtitle={`${ar ? "آخر تحديث" : "Last updated"} ${formatDate(
            restaurant.menuInfoAdded,
            lang,
          )}`}
          badge={ar ? "أساسي" : "Essential"}
          badgeVariant="danger"
          meta={ar ? "ملفات المنيو" : "Menu files"}
          status={
            restaurant.restaurantMenuFile ||
            restaurant.deliveryAppsMenuFile ||
            restaurant.foodCostSheetFile
              ? ar
                ? "محدّث"
                : "Updated"
              : ar
                ? "غير مرفوع"
                : "Not uploaded"
          }
          statusVariant={
            restaurant.restaurantMenuFile ||
            restaurant.deliveryAppsMenuFile ||
            restaurant.foodCostSheetFile
              ? "success"
              : "danger"
          }
          icon={<ExcelFile size={21} />}
        >
          <div
            className="lud-menu-files"
            onClick={(event) => event.stopPropagation()}
          >
            {[
              {
                key: "restaurantMenu",
                label: ar ? "منيو المطعم" : "Restaurant menu",
                file: restaurant.restaurantMenuFile,
                history: restaurant.restaurantMenuHistory || [],
                reference:
                  restaurant.restaurantMenuReference || "restaurantMenu",
                historyReference:
                  restaurant.restaurantMenuHistoryReference ||
                  restaurant.restaurantMenuReference ||
                  "restaurantMenu",
              },
              {
                key: "deliveryAppsMenu",
                label: ar ? "منيو تطبيقات التوصيل" : "Delivery apps menu",
                file: restaurant.deliveryAppsMenuFile,
                history: restaurant.deliveryAppsMenuHistory || [],
                reference: "deliveryAppsMenu",
                historyReference: "deliveryAppsMenu",
              },
              {
                key: "foodCostSheet",
                label: ar ? "ملف تكلفة الطعام" : "Food cost sheet",
                file: restaurant.foodCostSheetFile,
                history: restaurant.foodCostSheetHistory || [],
                reference: "foodCostSheet",
                historyReference: "foodCostSheet",
              },
            ].map((item) => {
              const selected = uploadTarget?.documentKey === item.key;
              const select = () =>
                onUploadTargetChange?.({ documentKey: item.key });
              return (
                <div
                  key={item.key}
                  className={`lud-menu-file-row ${selected ? "is-selected" : ""}`}
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  onClick={select}
                  onKeyDown={onActivateKey(select)}
                >
                  <div className="lud-menu-file-row__content">
                    <strong>{item.label}</strong>
                    {item.file ? (
                      fileLink(item.file, item.reference)
                    ) : (
                      <span className="lud-menu-file-row__empty">
                        {ar ? "لم يُرفع بعد" : "Not uploaded yet"}
                      </span>
                    )}

                    {item.history.length
                      ? historyLink(item.label, item.history, item.historyReference)
                      : null}
                  </div>

                  {selectionCheckbox(
                    item.key,
                    item.file,
                    item.history.length,
                    "lud-menu-file-row__checkbox",
                  )}
                </div>
              );
            })}
          </div>
        </DocumentCard>
      </div>

      <DocumentHistoryModal
        open={Boolean(historyTarget)}
        title={historyTarget?.title}
        history={(historyTarget?.history || []).map((entry) => {
          const historyFile = entry?.file || entry;
          return {
            ...entry,
            file: entry?.file
              ? {
                  ...entry.file,
                  originalName: getLudFileName({
                    file: historyFile,
                    kind: historyTarget?.kind,
                    reference: historyTarget?.reference,
                    originalName: historyFile?.originalName,
                  }),
                }
              : entry?.file,
            originalName: getLudFileName({
              file: historyFile,
              kind: historyTarget?.kind,
              reference: historyTarget?.reference,
              originalName: historyFile?.originalName,
            }),
          };
        })}
        lang={lang}
        onClose={() => setHistoryTarget(null)}
        onOpenFile={(entry) =>
          openLudFile(entry?.file || entry, {
            token,
            storeId: restaurant.id,
            kind: historyTarget?.kind,
            reference: historyTarget?.reference,
            historyId: entry?._id,
            onError,
          })
        }
      />

      <PermanentDeleteModal
        open={permanentDelete.confirmOpen}
        count={permanentDelete.selected.length}
        deleting={permanentDelete.deleting}
        error={permanentDelete.error}
        onConfirm={permanentDelete.confirm}
        onCancel={permanentDelete.closeConfirm}
      />

      {normalDelete.confirmOpen && (
        <DeleteModal
          id={null}
          loading={normalDelete.deleting}
          error={normalDelete.error}
          handleDelete={normalDelete.confirm}
          cancel={normalDelete.closeConfirm}
        />
      )}

      <NoticeBox variant="info">
        {ar
          ? 'هذه المستندات يضيفها العميل عادة أثناء التهيئة، ويمكن أيضاً رفعها أو تحديثها يدوياً من هذه الصفحة. عند رفع نسخة جديدة لأي مستند، القديم لا يُحذف — يتحول لحالة "ملغى" ويبقى بالسجل.'
          : "These documents are usually added by the client during onboarding and can also be uploaded or updated manually here. When a new version is uploaded, the old one is not deleted; it is marked as superseded and kept in history."}
      </NoticeBox>
    </>
  );
}
